import { Typography, SecondaryButton, PrimaryButton } from '@weareopenr/component-library';
import CollapsibleSection from 'src/components/CollapsibleSection';
import { IPublishFormValues, PublishType, Steps } from '../../types';
import styles from './SummaryView.module.scss';
import { usePriceScheme } from 'src/hooks/usePriceScheme';
import { ModalTitle } from '../../../ModalTitle/ModalTitle';
import { useExternalSystems } from '../../../../hooks/useExternalSystems';
import { useMemo } from 'react';
import { IntegrationType } from '../../../../constants';
import { ButtonContainer } from '../../../ButtonContainer/ButtonContainer';
import getPublishTypeLabel from '../../utilities/getPublishTypeLabel';
import { formatDateTime } from 'src/helpers';
import { createSectionNumberCounter } from '../../utilities/createSectionNumberCounter';

interface ISummaryViewProps {
  defaultValues: IPublishFormValues;
  submitDataToParent: (data: Partial<IPublishFormValues>, nextStep?: Steps) => void;
  onPublish: () => void;
  isLoading?: boolean;
}

export const SummaryView = ({
  defaultValues,
  submitDataToParent,
  onPublish,
  isLoading = false,
}: ISummaryViewProps): React.ReactElement => {
  const { data: priceScheme } = usePriceScheme(defaultValues.priceSchemeId);
  const { data: externalSystems } = useExternalSystems();

  const venuesMap = useMemo(() => {
    const venues =
      externalSystems?.find(
        (externalSystem) => externalSystem.integrationType === IntegrationType.POINT_OF_SALE_MANAGEMENT,
      )?.venues || [];
    return new Map(venues.map((venue) => [venue.venue_id, venue.name]));
  }, [externalSystems]);

  const includePricing =
    defaultValues.type === PublishType.PRICING || defaultValues.type === PublishType.PRODUCT_AND_PRICING;

  const numberOfVenues = defaultValues.venues?.length ?? 0;
  const numberOfExcludedItems = defaultValues.excludedItems?.length ?? 0;

  const getVenueName = (venueId: string): string => venuesMap.get(venueId) ?? venueId;

  const getSectionNumber = createSectionNumberCounter();

  return (
    <>
      <ModalTitle title="Publishing Summary" />
      <div className={styles['sections']}>
        <CollapsibleSection
          number={getSectionNumber()}
          title="Publish Type"
          subtitle={getPublishTypeLabel(defaultValues.type)}
        />
        {includePricing && (
          <>
            <CollapsibleSection number={getSectionNumber()} title="Price Scheme" subtitle={priceScheme?.name || ''} />
            <CollapsibleSection
              number={getSectionNumber()}
              title="Publish Venues"
              subtitle={`${numberOfVenues} ${numberOfVenues === 1 ? 'venue' : 'venues'} selected`}
            >
              {numberOfVenues > 0 && (
                <div className={styles['list-container']}>
                  {defaultValues.venues?.map((venueId) => (
                    <div key={venueId}>
                      <Typography>{getVenueName(venueId)}</Typography>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>
          </>
        )}

        <CollapsibleSection
          number={getSectionNumber()}
          title="Excluded Products"
          subtitle={`${numberOfExcludedItems} ${numberOfExcludedItems === 1 ? 'product' : 'products'} excluded`}
        >
          {numberOfExcludedItems > 0 && (
            <div className={styles['list-container']}>
              {defaultValues.excludedItems?.map((product) => (
                <div key={product.item_id}>
                  <Typography>{product.name}</Typography>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>
        <CollapsibleSection
          number={getSectionNumber()}
          title="Published date & time"
          subtitle={defaultValues.publicationDate ? formatDateTime(defaultValues.publicationDate) : 'Publish now'}
        />
      </div>
      <ButtonContainer
        left={
          <SecondaryButton
            onClick={() => submitDataToParent({}, Steps.schedule)}
            size="full-width"
            buttonStyle="outline"
          >
            Back
          </SecondaryButton>
        }
        right={
          <PrimaryButton onClick={onPublish} size="full-width" loading={isLoading}>
            Publish
          </PrimaryButton>
        }
      />
    </>
  );
};
